import { Controller } from "@hotwired/stimulus";
import { Dropzone } from "@deltablot/dropzone";
import { DirectUpload } from "@rails/activestorage";
import { loadPdfjs } from "../lib/pdfjs";

Dropzone.autoDiscover = false;

// Wraps a hidden <input type="file"> in a Dropzone drag-and-drop zone with
// previews. Each accepted file is uploaded straight to storage via Active Storage
// direct upload as soon as it's dropped; the returned signed blob id is added to
// the form as a hidden field, so the form submit carries ids (not bytes) and the
// server attaches by signed id. Supports single- and multi-file inputs.
export default class extends Controller {
  static targets = ["input", "zone"];
  static values = {
    maxFiles: { type: Number, default: 1 },
    maxFilesize: { type: Number, default: 20 },
    acceptedFiles: { type: String, default: ".jpg,.jpeg,.gif,.png,.webp" },
    message: { type: String, default: "Drop an image here or click to upload" },
    directUploadUrl: String,
    // Already-persisted attachments to show as previews: {name,size,url,deleteUrl,contentType,isImage}
    existingFiles: { type: Array, default: [] },
  };

  connect() {
    this.form = this.element.closest("form");
    this.pending = 0;
    this.failed = 0;
    this.dropzone = new Dropzone(this.zoneTarget, {
      url: this.directUploadUrlValue || this.form.action, // unused (autoProcessQueue off) but required
      paramName: this.inputTarget.name,
      maxFiles: this.maxFilesValue,
      maxFilesize: this.maxFilesizeValue,
      acceptedFiles: this.acceptedFilesValue,
      autoProcessQueue: false,
      addRemoveLinks: true,
      dictDefaultMessage: this.messageValue,
      dictRemoveFile: "Remove",
      dictInvalidFileType: "That file type isn't accepted here.",
    });

    // Dropzone only injects its default-message element when the zone carries the
    // literal `dropzone` class (dropzone.mjs:765), and both of our zones use their
    // own classes so the base stylesheet doesn't fight the custom look. Render the
    // message ourselves so dictDefaultMessage isn't silently dropped.
    if (!this.zoneTarget.querySelector(".dz-message")) {
      const message = document.createElement("div");
      message.className = "dz-message";
      const label = document.createElement("span");
      label.textContent = this.messageValue;
      message.appendChild(label);
      this.zoneTarget.appendChild(message);
    }

    this.dropzone.on("addedfile", (file) => {
      this.decorateNonImage(file);
      // accept() resolves after "addedfile", so defer: upload only real, accepted,
      // newly-dropped files (existing previews are mock objects, not File instances).
      if (file instanceof File && !file.existing) {
        Promise.resolve().then(() => {
          if (file.accepted) this.uploadFile(file);
          else this.refreshValidity();
        });
      }
    });
    this.dropzone.on("removedfile", (file) => {
      file.removed = true;
      if (file.hiddenInput) file.hiddenInput.remove();
      if (file.uploadFailed) {
        this.failed -= 1;
        this.updateSubmitButtons();
      }
      if (this.destroying) return;
      // Only a user clicking "Remove" deletes a saved file; destroy() (Turbo nav)
      // also emits removedfile for every file — the destroying guard skips those.
      if (file.existing && file.deleteUrl) this.deleteExisting(file.deleteUrl);
      this.refreshValidity();
    });
    this.dropzone.on("error", () => this.refreshValidity());

    // Single-file inputs replace the current file instead of erroring.
    if (this.maxFilesValue === 1) {
      this.dropzone.on("maxfilesexceeded", (file) => {
        this.dropzone.removeAllFiles();
        this.dropzone.addFile(file);
      });
    }

    // Block advancing while a file is rejected or still uploading.
    this.guardSubmit = this.guardSubmit.bind(this);
    this.form.addEventListener("submit", this.guardSubmit);

    this.hideFileInput();
    this.loadExistingFiles();
  }

  // Upload one file to storage, showing a progress bar, then record its signed id.
  uploadFile(file) {
    const fill = this.addProgressBar(file);
    const startedAt = performance.now();
    const delegate = {
      directUploadWillStoreFileWithXHR(xhr) {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable && fill)
            fill.style.width = `${Math.round((event.loaded / event.total) * 100)}%`;
        });
      },
    };

    const upload = new DirectUpload(file, this.directUploadUrlValue, delegate);
    this.pending += 1;
    this.updateSubmitButtons();
    upload.create((error, blob) => {
      this.pending -= 1;
      if (fill) fill.style.width = "100%";
      // Keep the bar up for a beat so a fast (localhost) upload doesn't just flash.
      const clearBar = () =>
        file.previewElement?.querySelector(".dz-upload-progress")?.remove();
      setTimeout(clearBar, Math.max(0, 600 - (performance.now() - startedAt)));

      if (file.removed) {
        clearBar();
      } else if (error) {
        this.failed += 1;
        file.uploadFailed = true;
        file.previewElement?.classList.add("dz-upload-failed");
      } else {
        this.appendSignedId(file, blob.signed_id);
        file.previewElement?.classList.add("dz-uploaded");
      }
      this.updateSubmitButtons();
      this.refreshValidity();
    });
  }

  // Disable the form's forward submit button(s) while any upload is in flight or a
  // file failed to upload — you can't advance until every file has its signed id.
  // Back stays enabled.
  updateSubmitButtons() {
    const disabled = this.pending > 0 || this.failed > 0;
    this.form
      .querySelectorAll('button[type="submit"], input[type="submit"]')
      .forEach((button) => {
        if (button.value !== "back") button.disabled = disabled;
      });
  }

  addProgressBar(file) {
    if (!file.previewElement) return null;
    const bar = document.createElement("div");
    bar.className = "dz-upload-progress";
    const fill = document.createElement("span");
    bar.appendChild(fill);
    file.previewElement.appendChild(bar);
    return fill;
  }

  appendSignedId(file, signedId) {
    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = this.inputTarget.name;
    hidden.value = signedId;
    this.form.appendChild(hidden);
    file.hiddenInput = hidden;
  }

  // Render already-saved attachments as previews (image thumbnails, PDF first-page
  // renders) with a working Remove link. They are mock files and carry no signed
  // id — they're already attached server-side.
  loadExistingFiles() {
    this.existingFilesValue.forEach((info) => {
      const mock = {
        name: info.name,
        size: info.size,
        type: info.contentType,
        accepted: true,
        existing: true,
        deleteUrl: info.deleteUrl,
      };
      if (!info.isImage) {
        mock.arrayBuffer = () =>
          fetch(info.url, { credentials: "same-origin" }).then((response) =>
            response.arrayBuffer(),
          );
      }

      this.dropzone.files.push(mock);
      this.dropzone.emit("addedfile", mock);
      if (info.isImage) this.dropzone.emit("thumbnail", mock, info.url);
      this.dropzone.emit("complete", mock);
      mock.previewElement?.classList.add("dz-uploaded"); // already saved → show the check
    });
    this.dropzone._updateMaxFilesReachedClass();
  }

  deleteExisting(url) {
    fetch(url, {
      method: "DELETE",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": this.csrfToken() },
    });
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }

  guardSubmit(event) {
    // "Back" is always allowed; only block forward moves.
    if (event.submitter && event.submitter.value === "back") return;

    if (this.dropzone.getRejectedFiles().length > 0) {
      this.blockSubmit(
        event,
        "One or more files can't be uploaded — remove the ones marked in red " +
          "(only PDF, JPG, or PNG up to 20 MB) before continuing.",
      );
    } else if (this.pending > 0) {
      this.blockSubmit(
        event,
        "Please wait for your uploads to finish before continuing.",
      );
    } else if (this.failed > 0) {
      this.blockSubmit(
        event,
        "Some files failed to upload — remove them and try again.",
      );
    }
  }

  blockSubmit(event, message) {
    event.preventDefault();
    event.stopPropagation();
    this.showError(message);
  }

  showError(message) {
    if (!this.errorElement) {
      this.errorElement = document.createElement("div");
      this.errorElement.className = "text-danger small mt-2";
      this.zoneTarget.insertAdjacentElement("afterend", this.errorElement);
    }
    this.errorElement.textContent = message;
    this.errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  refreshValidity() {
    if (
      this.dropzone.getRejectedFiles().length === 0 &&
      this.pending === 0 &&
      this.failed === 0 &&
      this.errorElement
    ) {
      this.errorElement.remove();
      this.errorElement = null;
    }
  }

  hideFileInput() {
    this.inputTarget.disabled = true;
    this.inputTarget.style.display = "none";
  }

  // Non-image files get no thumbnail, so tag the preview and stash the
  // extension for the CSS fallback icon/label. For PDFs, try to replace that
  // fallback with a rendered image of the first page.
  decorateNonImage(file) {
    if (!file.previewElement || (file.type || "").startsWith("image/")) return;

    file.previewElement.classList.add("dz-doc");
    const image = file.previewElement.querySelector(".dz-image");
    if (!image) return;

    image.dataset.ext = (file.name.split(".").pop() || "file").toUpperCase();

    if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) {
      this.renderPdfThumbnail(file, image);
    }
  }

  // Render page 1 to a canvas entirely in the browser and use it as the
  // preview thumbnail. On any failure, the CSS document-icon fallback remains.
  async renderPdfThumbnail(file, image) {
    try {
      const pdfjs = await loadPdfjs();
      const data = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data }).promise;
      const page = await pdf.getPage(1);

      const canvas = document.createElement("canvas");
      const scale = 220 / page.getViewport({ scale: 1 }).width;
      const viewport = page.getViewport({ scale });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: canvas.getContext("2d"), viewport })
        .promise;

      const img = image.querySelector("img") || document.createElement("img");
      img.src = canvas.toDataURL("image/png");
      if (!img.parentElement) image.appendChild(img);

      // Swap from the icon fallback to the rendered first page.
      file.previewElement.classList.remove("dz-doc");
      file.previewElement.classList.add("dz-pdf-thumb");
      pdf.destroy();
    } catch (error) {
      console.warn("PDF thumbnail render failed", error);
    }
  }

  disconnect() {
    // Guard the removedfile handler: destroy() emits it for every remaining file.
    this.destroying = true;
    if (this.form && this.guardSubmit) {
      this.form.removeEventListener("submit", this.guardSubmit);
    }
    if (this.dropzone) {
      this.dropzone.destroy();
    }
  }
}
